/**
 * The sidebar identity block's name/initials (M-1b, live round 2026-09-12).
 *
 * `ra-surface.component.ts`'s `Identity` getter used to derive BOTH straight
 * from `Metadata().CurrentUser.Name` — the MJ `User` row, which for a
 * ForgeRock-provisioned account is the sign-in e-mail — while `RoleLine`
 * (same object, same paint) already read the linked member's own profile
 * correctly (`Assistant Professor · Brigham Young University`). Observed:
 * `.agu-sidebar__name` → `AGU member` / `AG`, one paint after Home, the
 * profile card and Settings had all rendered `Ben W Abbott` from the exact
 * same server round trip (`researchMyProfile`'s `memberName` field, once
 * `LoadRoleLine` resolves it).
 *
 * Extracted as a pure function so the priority chain — server `memberName`,
 * then the `CurrentUser` name fields, then the generic fallback — is testable
 * without mounting the component or a `Metadata()` singleton.
 */
export interface SidebarIdentity {
    readonly Name: string;
    readonly Initials: string;
}

/**
 * The MJ user's name fields the fallback reads. `Metadata().CurrentUser`
 * (`UserInfo`) satisfies it as-is; the fields are nullable because MJ leaves
 * them empty for some providers.
 */
export interface SidebarUserName {
    readonly FirstName: string | null | undefined;
    readonly LastName: string | null | undefined;
    readonly Name: string | null | undefined;
}

/** What the block shows when no source carries a usable name — the server's `UNKNOWN_MEMBER_NAME` (apps/MJAPI `names.ts`). */
const UNKNOWN_MEMBER_NAME = 'AGU member';
const UNKNOWN_MEMBER_INITIALS = 'AG';

/** First letters of the first two whitespace-separated name tokens, uppercase. Mirrors apps/MJAPI's `names.ts` `Initials` (server), kept independent — the two apps don't share a runtime. */
function initialsOf(name: string): string {
    return name
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((p) => p[0]?.toUpperCase() ?? '')
        .join('');
}

/**
 * One name part, whitespace-collapsed, or null when blank or an e-mail (MJ
 * writes the sign-in e-mail into `User.Name` and, for some providers,
 * `FirstName`). Mirrors the server's `NormalizeName` + `UsableName`.
 */
function usableName(raw: string | null | undefined): string | null {
    const collapsed = (raw ?? '').replace(/\s+/g, ' ').trim();
    return collapsed.length > 0 && !collapsed.includes('@') ? collapsed : null;
}

/**
 * The sidebar's name and initials, by the one name rule every RA surface
 * follows (B-20, 2026-10-05 stage sanity — user decision): the linked
 * AguPerson name, else the MJ user's FirstName/LastName (then a non-e-mail
 * `User.Name`), and `AGU member` only when nothing usable exists.
 *
 * `memberName` is the server-supplied `researchMyProfile.memberName`
 * (`MemberFullName` in apps/MJAPI's `names.ts`), which already applies that
 * whole chain — for staff too. It is null until `LoadRoleLine` resolves, so
 * `user` (`Metadata().CurrentUser`) supplies the same chain's MJ-user half in
 * the meantime: an e-mail-named account used to read `AGU member` / `AG` here
 * while the greeting and My profile named the person.
 */
export function SidebarIdentityFrom(memberName: string | null, user: SidebarUserName): SidebarIdentity {
    const fromMember = usableName(memberName);
    const fromUserParts = [usableName(user.FirstName), usableName(user.LastName)].filter((p): p is string => p !== null).join(' ');
    const name = fromMember ?? (fromUserParts || usableName(user.Name));
    if (!name) return { Name: UNKNOWN_MEMBER_NAME, Initials: UNKNOWN_MEMBER_INITIALS };
    return { Name: name, Initials: initialsOf(name) || UNKNOWN_MEMBER_INITIALS };
}
